import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import moment from 'moment'
import NumberFormat from 'react-number-format'
import { useTranslation } from 'react-i18next'

import {
  Autocomplete,
  Dialog,
  CircularProgress,
  Typography,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  IconButton,
  Button,
  Tooltip,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import { Cancel } from '@material-ui/icons'
import { styled } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TextField from "@material-ui/core/TextField"

import CoafDateRangePicker from './CoafDateRangePicker'
import CoafMoneyTextField from './CoafMoneyTextField'
import CoafClauseNumberList from './CoafClauseNumberList'
import OccurrenceInvolvedPartiesTable from './OccurrenceInvolvedPartiesTable'
import { validateCNPJ, validateCPF } from "../utils/utils"

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
    border: 'none',
    '&:before': {
        display: 'none',
    },
}))

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        {...props}
    />
))(({ theme }) => ({
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: 0,
    flexDirection: 'row',
    '& .MuiAccordionSummary-content': {
        marginLeft: 0,
    },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2)
}))

const SectionHeader = styled('div')(({ theme }) => ({
    marginTop: theme.spacing(3),
    borderBottom: `2px solid ${theme.palette.divider}`,
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
}))

function CoafOccurrenceDialog(props) {
    const { t } = useTranslation()
    const { occurrence, occurrenceDialogState, setOccurrenceDialogState, setSnackbarContent, refreshList } = props
    const { open, mode } = occurrenceDialogState

    const [occurrenceData, setOccurrenceData] = useState(null)
    const [expanded, setExpanded] = useState({
        transactions: false,
        parties: true,
    })
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [errorState, setErrorState] = useState({})
    const [confirmExitOpen, setConfirmExitOpen] = useState(false)

    const trimNestedObject = (input) => {
        const isPlainObject = (val) => (
            val !== null &&
            Object.prototype.toString.call(val) === "[object Object]" &&
            (Object.getPrototypeOf(val) === Object.prototype || Object.getPrototypeOf(val) === null)
        )

        if (!(Array.isArray(input) || isPlainObject(input))) return input

        const root = Array.isArray(input) ? [] : {}
        const stack = [{ source: input, target: root }]

        const assignValue = (container, key, value) => {
            if (typeof value === "string") {
                container[key] = value.trim()
            } else if (Array.isArray(value)) {
                const next = []
                container[key] = next
                stack.push({ source: value, target: next })
            } else if (isPlainObject(value)) {
                const next = {}
                container[key] = next
                stack.push({ source: value, target: next })
            } else if (value === null || value === undefined) {
                delete container[key]
            } else {
                container[key] = value
            }
        }

        while (stack.length) {
            const { source, target } = stack.pop()

            if (Array.isArray(source)) {
                source.forEach((value, index) => assignValue(target, index, value))
            } else {
                Object.entries(source).forEach(([key, value]) => assignValue(target, key, value))
            }
        }
        return root
    }

    const sanitizeOccurrenceData = (data) => {
        const {involved_parties, ...rest} = data || {}

        const sanitizedParties = involved_parties?.map(party => {
            const {_uiId, ...sanitizedParty} = party
            return sanitizedParty
        })

        const payload = {...rest, involved_parties: sanitizedParties}
        const trimmedPayload = trimNestedObject(payload)
        return trimmedPayload
    }

    const generateKey = () => Math.random().toString(36).substring(2, 15)

    const hidrateOccurrenceDataKeys = useCallback((data) => {
        const {involved_parties, ...rest} = data || {}

        const partiesWithKey = involved_parties?.map(party => {
            return { ...party, _uiId: generateKey() }
        })

        return {...rest, involved_parties: partiesWithKey}
    }, [])

    const handleClose = () => {
        setOccurrenceDialogState({ open: false, mode: "view", occurrence: null })
        setOccurrenceData(null)
        setErrorState({})
        setConfirmExitOpen(false)
        if (refreshList) refreshList()
    }

    const handleAttemptClose = () => {
        if (mode === 'view') {
            handleClose()
        } else {
            setConfirmExitOpen(true)
        }
    }

    const fetchOccurrenceData = useCallback(async (occurrence_key) => {
        setIsLoading(true)
        try {
            let response = await axios.get(`/dash/alerts/occurrence/${occurrence_key}`)
            return response.data
        } catch (error) {
            setSnackbarContent({
                type: "error",
                message: "Ocorreu um erro ao buscar os dados da ocorrência. Por favor, tente novamente."
            })
        } finally {
            setIsLoading(false)
        }
    }, [setSnackbarContent])

    useEffect(() => {
        if (!open) return

        if (mode === "create") {
            setOccurrenceData({
                external_id: "",
                start_date: null,
                end_date: null,
                branch_number: "",
                branch_name: "",
                branch_city: "",
                branch_state: "",
                credit_amount: 0,
                debit_amount: 0,
                provisioning_amount: 0,
                proposal_amount: 0,
                clause_numbers: [],
                analysis_description: "",
                communicator_document_number: "",
                involved_parties: []
            })
        } else if (occurrence) {
            const fetch = async () => {
                const data = await fetchOccurrenceData(occurrence.occurrence_key)
                if(data) {
                    setOccurrenceData(hidrateOccurrenceDataKeys(data.occurrence_data))
                }
            }
            fetch()
        }
        setErrorState({})
    }, [open, mode, occurrence, fetchOccurrenceData, hidrateOccurrenceDataKeys])


    const getMomentDate = (date, nullCase) =>{
        if (date == null) return nullCase
        else return moment(date)
    }

    const normalizeDateValue = (date) => {
        if (!date) return date
        if (typeof date === 'string') return date
        if (moment.isMoment(date) || date instanceof Date) return moment(date).toISOString()
        return date
    }

    const handleInputChange = (field) => (event) => {
        const value = event.target.value
        setOccurrenceData((prev) => ({
            ...prev,
            [field]: value,
        }))
        if (errorState[field]) setErrorState(prev => ({...prev, [field]: null}))
    }

    const handleValueChange = (field) => (value) => {
        setOccurrenceData((prev) => ({
            ...prev,
            [field]: value,
        }))
        if (errorState[field]) setErrorState(prev => ({...prev, [field]: null}))
    }

    const handleMoneyValueChange = (field) => (value) => {
        setOccurrenceData((prev) => ({
            ...prev,
            [field]: parseInt(value.value),
        }))
    }

    const validateForm = () => {
        const errors = {}
        const data = occurrenceData

        if (!data.external_id || !data.external_id.trim()) errors.external_id = true
        if (!data.start_date) errors.start_date = true
        if (!data.end_date) errors.end_date = true

        const commDoc = (data.communicator_document_number || "").replace(/\D/g, "")
        if (!commDoc) {
            errors.communicator_document_number = t("Obrigatório")
        } else if (commDoc.length <= 11) {
            if (!validateCPF(commDoc)) errors.communicator_document_number = t("CPF Inválido")
        } else {
            if (!validateCNPJ(commDoc)) errors.communicator_document_number = t("CNPJ Inválido")
        }

        if (!data.branch_number) errors.branch_number = true
        if (!data.branch_name) errors.branch_name = true
        if (!data.branch_city) errors.branch_city = true
        if (!data.branch_state) errors.branch_state = true

        if (!data.clause_numbers || data.clause_numbers.length === 0) errors.clause_numbers = true

        if (!data?.involved_parties.some(party => party.type === 'holder')) {
            errors.involved_parties = true
            setExpanded(prev => ({...prev, parties: true}))
        }

        if (!data.involved_parties || data.involved_parties.length === 0) {
            errors.involved_parties = true
            setExpanded(prev => ({...prev, parties: true}))
        }

        return errors
    }

    const handleSave = async () => {
        const errors = validateForm()
        if (Object.keys(errors).length > 0) {
            setErrorState(errors)
            return
        }

        setIsSaving(true)
        try {
            if (mode === 'create') {
                await axios.post(`/dash/alerts/occurrence`, {occurrence_data: sanitizeOccurrenceData(occurrenceData)})
            } else {
                await axios.put(`/dash/alerts/occurrence/${occurrence.occurrence_key}`, {occurrence_data: sanitizeOccurrenceData(occurrenceData)})
            }
            handleClose()
        } catch (error) {
            if (error.response.status === 400 && error.response.data.title === "Duplicate occurrence external_id") {
                setSnackbarContent({
                    type: "error",
                    message: "Já existe uma ocorrência com o mesmo identificador. Por favor, utilize um identificador único."
                })
            } else {
                setSnackbarContent({
                    type: "error",
                    message: "Ocorreu um erro ao salvar a ocorrência. Por favor tente novamente mais tarde."
                })
            }
        } finally {
            setIsSaving(false)
        }
    }

    const modeVerbDict = {
        create: t("Criar"),
        edit: t("Editar"),
        view: t("Visualizar"),
    }
    const modeVerb = modeVerbDict[mode] || t("Visualizar")
    const readOnly = mode === "view"

    const ufList = [
            "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA",
            "MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN",
            "RS","RO","RR","SC","SP","SE","TO"
          ]

    return (
        <>
        <Dialog
            open={open}
            onClose={handleAttemptClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ style: { padding: "20px 25px" } }}
        >
            <div style={{ display: "flex", marginBottom: "12px", justifyContent: "space-between" }}>
                <span className={["blueText", "titleSize"].join(" ")}>
                    {[modeVerb, t("Ocorrência")].join(' ')}
                </span>
                <IconButton onClick={handleAttemptClose}>
                    <Cancel />
                </IconButton>
            </div>
            {isLoading && (
                <div style={{ width: "auto" }}>
                    <div className='circularProgressCenter'>
                        <CircularProgress />
                    </div>
                </div>
            )}
            {!isLoading && (
            <div>
                <Accordion
                    expanded={expanded?.parties}
                    onChange={(_, isExpanded) => setExpanded((prev) => ({...prev, parties: isExpanded}))}
                    style={{ border: errorState.involved_parties ? "1px solid red" : "none" }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography className="blueText" style={{ fontWeight: 'bold', color: errorState.involved_parties ? "red" : "#0b1f82" }}>
                            {t("Partes Envolvidas")} {errorState.involved_parties ? `(${t("Obrigatório adicionar pelo menos uma parte titular")})` : ""}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <OccurrenceInvolvedPartiesTable
                            involvedParties={occurrenceData?.involved_parties}
                            onChange={handleValueChange('involved_parties')}
                            readOnly={readOnly}
                        />
                    </AccordionDetails>

                </Accordion>
                <SectionHeader>
                    <Typography className="blueText" style={{ fontWeight: 'bold' }}>
                        {t("Dados da Ocorrência")}
                    </Typography>
                </SectionHeader>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{width: "80%", display: "flex", flexDirection: "column", alignContent: "left"}}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <SectionHeader style={{ minHeight: '36px'}}>
                                <Typography className="blueText">
                                    {t("Informações Gerais")}
                                </Typography>
                            </SectionHeader>
                            <TextField
                                label={t("Nome da Ocorrência")}
                                value={occurrenceData?.external_id || ""}
                                onChange={handleInputChange('external_id')}
                                InputProps={{
                                    readOnly: readOnly,
                                }}
                                error={!!errorState.external_id}
                                helperText={errorState.external_id ? t("Campo obrigatório") : ""}
                            />
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px"}}>
                                <div style={{width: '50%'}}>
                                    <CoafDateRangePicker
                                        label={t("Período (Início - Fim)")}
                                        startDate={getMomentDate(occurrenceData?.start_date, null)}
                                        endDate={getMomentDate(occurrenceData?.end_date, null)}
                                        onDatesChange={({startDate, endDate}) => {
                                            setOccurrenceData(prev => ({
                                                ...prev,
                                                start_date: normalizeDateValue(startDate),
                                                end_date: normalizeDateValue(endDate)
                                            }))
                                            if(errorState.start_date) setErrorState(prev => ({...prev, start_date: null}))
                                            if(errorState.end_date) setErrorState(prev => ({...prev, end_date: null}))
                                        }}
                                        readOnly={readOnly}
                                        error={!!errorState.start_date || !!errorState.end_date}
                                    />
                                    {errorState.start_date && <span style={{color: 'red', fontSize: '12px'}}>{t("Data inicial obrigatória")}</span>}
                                </div>
                                <Tooltip title={errorState.communicator_document_number || ""} open={!!errorState.communicator_document_number} placement="top" arrow>
                                    <div style={{width: '50%'}}>
                                        <NumberFormat
                                            customInput={TextField}
                                            label={t("Documento do Comunicador")}
                                            placeholder="CPF/CNPJ"
                                            value={occurrenceData?.communicator_document_number}
                                            format={
                                                occurrenceData?.communicator_document_number?.replace(/\D/g, "").length > 11 ? "##.###.###/####-##" : "###.###.###-#####"
                                            }
                                            onValueChange={(values) => {
                                                setOccurrenceData(prev => ({ ...prev, communicator_document_number: values.formattedValue }))
                                                if(errorState.communicator_document_number) setErrorState(prev => ({...prev, communicator_document_number: null}))
                                            }}
                                            error={!!errorState.communicator_document_number}
                                            InputProps={{ readOnly: readOnly }}
                                            fullWidth
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px"}}>
                            <SectionHeader style={{ minHeight: '36px'}}>
                                <Typography className="blueText">
                                    {t("Informações da Agência")}
                                </Typography>
                            </SectionHeader>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px"}}>
                                <NumberFormat
                                    value={occurrenceData?.branch_number}
                                    customInput={TextField}
                                    allowNegative={false}
                                    thousandSeparator={""}
                                    onValueChange={(values) => {
                                        setOccurrenceData(prev => ({ ...prev, branch_number: values.value }))
                                        if(errorState.branch_number) setErrorState(prev => ({...prev, branch_number: null}))
                                    }}
                                    placeholder={"0000"}
                                    decimalScale={0}
                                    fixedDecimalScale={true}
                                    label={t("Nº da Agência")}
                                    InputLabelProps={{ shrink: true, readOnly: readOnly}}
                                    inputProps={{readOnly: readOnly}}
                                    allowLeadingZeros={true}
                                    format={"####"}
                                    onBlur={() => {
                                        if (occurrenceData.branch_number) {
                                            setOccurrenceData(prev => ({ ...prev, branch_number: occurrenceData.branch_number.padStart(4, "0") }))
                                        }
                                    }}
                                    readOnly={readOnly}
                                    fullWidth
                                    style={{ width: "25%" }}
                                    error={!!errorState.branch_number}
                                />
                                <TextField
                                    label={t("Nome da Agência")}
                                    value={occurrenceData?.branch_name || ""}
                                    onChange={handleInputChange('branch_name')}
                                    InputProps={{
                                        readOnly: readOnly,
                                    }}
                                    fullWidth
                                    style={{ flex: 1}}
                                    error={!!errorState.branch_name}
                                />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px"}}>
                                <TextField
                                    label={t("Cidade da Agência")}
                                    value={occurrenceData?.branch_city || ""}
                                    onChange={handleInputChange('branch_city')}
                                    InputProps={{
                                        readOnly: readOnly,
                                    }}
                                    fullWidth
                                    style={{ flex: 1}}
                                    error={!!errorState.branch_city}
                                />
                                <Autocomplete
                                    options={ufList}
                                    value={occurrenceData?.branch_state || null}
                                    onChange={(event, newValue) => handleValueChange('branch_state')(newValue)}
                                    readOnly={readOnly}
                                    renderInput={(params) => (
                                         <TextField
                                            {...params}
                                            label={t("UF")}
                                            InputProps={{
                                                ...params.InputProps,
                                                readOnly: readOnly,
                                            }}
                                            style={{ bottom: "-4px" }}
                                            className="filterTextbox"
                                            error={!!errorState.branch_state}
                                        />
                                    )}
                                    fullWidth
                                    style={{ width: "18%" }}
                                />
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px"}}>
                            <SectionHeader style={{ minHeight: '36px'}}>
                                <Typography className="blueText">
                                    {t("Valores")}
                                </Typography>
                            </SectionHeader>
                            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", rowGap: "10px"}}>
                                <CoafMoneyTextField
                                    label={t("Valor Creditado")}
                                    value={occurrenceData?.credit_amount}
                                    onChange={handleMoneyValueChange('credit_amount')}
                                    readOnly={readOnly}
                                />
                                <CoafMoneyTextField
                                    label={t("Valor Debitado")}
                                    value={occurrenceData?.debit_amount}
                                    onChange={handleMoneyValueChange('debit_amount')}
                                    readOnly={readOnly}
                                />
                                <CoafMoneyTextField
                                    label={t("Valor Provisionado")}
                                    value={occurrenceData?.provisioning_amount}
                                    onChange={handleMoneyValueChange('provisioning_amount')}
                                    readOnly={readOnly}
                                />
                                <CoafMoneyTextField
                                    label={t("Valor Proposto")}
                                    value={occurrenceData?.proposal_amount}
                                    onChange={handleMoneyValueChange('proposal_amount')}
                                    readOnly={readOnly}
                                />
                            </div>
                            <div>
                                <SectionHeader style={{ minHeight: '36px'}}>
                                    <Typography className="blueText">
                                        {t("Detalhamento da Ocorrência")}
                                    </Typography>
                                </SectionHeader>
                                <div style={{ border: errorState.clause_numbers ? "1px solid red" : "none", borderRadius: "4px" }}>
                                    <CoafClauseNumberList
                                            value={occurrenceData?.clause_numbers}
                                            onChange={(val) => {
                                                handleValueChange('clause_numbers')(val)
                                                if(val && val.length > 0) setErrorState(prev => ({...prev, clause_numbers: null}))
                                            }}
                                            readOnly={readOnly}
                                    />
                                    {errorState.clause_numbers && <span style={{color: 'red', fontSize: '12px', marginLeft: '10px'}}>{t("Selecione ao menos um enquadramento")}</span>}
                                </div>
                                <TextField
                                    label={t("Descrição")}
                                    value={occurrenceData?.analysis_description || ""}
                                    onChange={handleInputChange('analysis_description')}
                                    InputProps={{
                                        readOnly: readOnly,
                                    }}
                                    multiline
                                    minRows={4}
                                    fullWidth
                                    style={{ marginTop: "10px" }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {!readOnly && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
                        <Button
                            variant="contained"
                            className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                            style={{ minWidth: '150px', borderRadius: '18px' }}
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? <CircularProgress size={24} color="inherit" /> : t("Confirmar dados")}
                        </Button>
                    </div>
                )}
            </div>
            )}
        </Dialog>
        {confirmExitOpen && <Dialog
            open={confirmExitOpen}
            onClose={() => setConfirmExitOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                {t("Tem certeza que deseja fechar?")}
            </DialogTitle>
            <DialogContent>
                <Typography id="alert-dialog-description">
                    {t("Você perderá os dados novos/alterados. Deseja continuar?")}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirmExitOpen(false)}
                        className="button onlyboarder normalText normalMediumSize">
                    {t("Não, continuar editando")}
                </Button>
                <Button onClick={handleClose}
                        className="button standard normalText normalMediumSize">
                    {t("Sim, fechar")}
                </Button>
            </DialogActions>
        </Dialog>}
        </>
    )
}

export default CoafOccurrenceDialog